// src/components/FilterDropdown.jsx
//
// Reusable filter-dropdown trigger + popover. Visual parity with the
// legacy FilterDropdownBar, factored so PortfolioView (and future filter
// bars) can compose 1..N of them without copy-pasting markup.
//
// Open/close is owned by the parent so a sibling click can close any
// currently-open dropdown ("only one open at a time"). Click-outside
// + Escape detection also lives at the parent (P7) — see the parent
// useEffect in PortfolioView.jsx — and finds dropdowns via the
// `data-dropdown-wrapper` / `data-dropdown-trigger` attributes added
// to the outer wrapper and trigger button below.
//
// Props:
//   id          — short string used in data-testid (e.g. "stage")
//   label       — small label shown to the left of the value ("Stage")
//   currentVal  — formatted display value for the current selection
//   isFiltered  — non-default selection → blue accent
//   options     — Array<{ value, label, count, dotColor? }
//                        | { type: "divider", caption?: string }
//                  Divider entries render a horizontal rule + optional
//                  ALL-CAPS caption; they are non-selectable and skipped by
//                  keyboard navigation. Backward-compatible: callers with no
//                  divider entries render exactly as before.
//                  dotColor (optional, P3): small filled circle before label.
//   selectedValue — value to highlight in the popover
//   isOpen      — boolean (parent-controlled)
//   onToggle    — () => void  (open/close)
//   onSelect    — (value) => void
//   align       — "left" (default) | "right"
//                 When "right", the menu is rendered via a portal to
//                 document.body with position:fixed so it never clips
//                 against parent overflow constraints or the viewport edge.
//                 All existing callers (align omitted → "left") are unchanged.
//   triggerMaxWidth — optional override for long displayed values (e.g. full month names).

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { T } from "../app/tokens.js";

export function FilterDropdown({
  id,
  label,
  currentVal,
  isFiltered,
  options = [],
  selectedValue,
  isOpen,
  onToggle,
  onSelect,
  align = "left",
  triggerMaxWidth,
}) {
  // Separate selectable options for keyboard navigation.
  // Keyboard focus only cycles through selectable entries; dividers are skipped.
  const sOptions = options.filter((o) => o.type !== "divider");

  // M3.2: combobox/listbox keyboard model.
  //   - Trigger button: Enter/Space toggles open (native button behavior).
  //   - Open panel: ArrowDown/Up navigates (wraps); Enter selects; Esc
  //     closes + returns focus to trigger.
  //   - Initial focus lands on the currently selected option, or first
  //     when no selection.
  // Tab is intentionally NOT trapped — it falls through to whatever
  // comes next in document order, per WAI-ARIA listbox guidance.
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);
  const [focusedIdx, setFocusedIdx] = useState(0);

  // Portal positioning state (only used when align="right").
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (!isOpen) return;
    const sel = sOptions.findIndex((o) => o.value === selectedValue);
    const init = sel >= 0 ? sel : 0;
    setFocusedIdx(init);
    // Defer focus until after the panel has rendered.
    const raf = requestAnimationFrame(() => {
      optionRefs.current[init]?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen]); // intentional: focus effect runs once when dropdown opens, not on every handler re-render

  // Portal path: compute fixed position from trigger's bounding rect.
  // useLayoutEffect so the position is set before the browser paints.
  const computeMenuPos = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 6,
      right: window.innerWidth - rect.right,
    });
  };

  useLayoutEffect(() => {
    if (align !== "right" || !isOpen) return;
    computeMenuPos();
  }, [align, isOpen]);

  // Reposition on window resize while the menu is open.
  useEffect(() => {
    if (align !== "right" || !isOpen) return;
    const handler = () => computeMenuPos();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [align, isOpen]);

  const onListKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = (focusedIdx + 1) % sOptions.length;
      setFocusedIdx(next);
      optionRefs.current[next]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const next = (focusedIdx - 1 + sOptions.length) % sOptions.length;
      setFocusedIdx(next);
      optionRefs.current[next]?.focus();
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const opt = sOptions[focusedIdx];
      if (opt) {
        onSelect(opt.value);
        triggerRef.current?.focus();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      onToggle();
      triggerRef.current?.focus();
    }
  };

  const menuNode = isOpen ? (
    <div
      role="listbox"
      data-testid={`filter-dropdown-${id}-menu`}
      onKeyDown={onListKeyDown}
      style={
        align === "right"
          ? {
              position: "fixed",
              top: menuPos.top,
              right: menuPos.right,
              background: T.white, border: `1.5px solid ${T.border}`,
              borderRadius: 10, boxShadow: T.shadowDropdown,
              zIndex: 1000, width: "max-content",
              maxWidth: 360, maxHeight: 340, overflowY: "auto",
              padding: 6,
            }
          : {
              position: "absolute", top: "calc(100% + 6px)", left: 0,
              background: T.white, border: `1.5px solid ${T.border}`,
              borderRadius: 10, boxShadow: T.shadowDropdown,
              zIndex: 200, minWidth: "100%", width: "max-content",
              maxWidth: 360, maxHeight: 340, overflowY: "auto",
              padding: 6,
            }
      }
    >
      {(() => {
        // Track selectable index separately so optionRefs align with the
        // sOptions array used by keyboard navigation.
        let si = -1;
        return options.map((opt, i) => {
          if (opt.type === "divider") {
            return (
              <div
                key={`divider-${i}`}
                aria-hidden="true"
                style={{ margin: "4px 0" }}
              >
                {!opt.noLine && <div style={{ borderTop: `1px solid ${T.border}`, margin: "0 4px" }} />}
                {opt.caption && (
                  <div
                    data-testid={`filter-dropdown-${id}-section-${opt.caption.toLowerCase().replace(/\s+/g, "-")}`}
                    style={{
                      padding: "4px 10px 2px",
                      fontSize: T.fsXxs, fontWeight: T.fwBold,
                      color: T.light, letterSpacing: "0.06em",
                      textTransform: "uppercase",
                    }}
                  >
                    {opt.caption}
                  </div>
                )}
              </div>
            );
          }
          si += 1;
          const mySi = si;
          const sel = opt.value === selectedValue;
          return (
            <div
              key={opt.value}
              ref={(el) => { optionRefs.current[mySi] = el; }}
              role="option"
              aria-selected={sel}
              tabIndex={-1}
              data-testid={`filter-dropdown-${id}-option-${opt.value}`}
              onClick={() => onSelect(opt.value)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "7px 10px", borderRadius: 6,
                fontSize: T.fsSm5, fontWeight: sel ? T.fwSemibold : T.fwRegular,
                color: sel ? T.blue : T.dark,
                background: sel ? T.blueSoft : "transparent",
                cursor: "pointer", gap: 14,
                whiteSpace: "nowrap",
                transition: "background 0.1s",
              }}
              onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = T.lightGray; }}
              onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                {opt.dotColors ? (
                  <span
                    data-testid={`filter-dropdown-${id}-option-${opt.value}-dot`}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0 }}
                  >
                    {opt.dotColors.map((c, ci) => (
                      <span key={ci} data-testid={`filter-dropdown-${id}-option-${opt.value}-dot-${ci}`} style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: c }} />
                    ))}
                  </span>
                ) : opt.dotColor ? (
                  <span
                    data-testid={`filter-dropdown-${id}-option-${opt.value}-dot`}
                    style={{
                      display: "inline-block",
                      width: 8, height: 8, borderRadius: "50%",
                      background: opt.dotColor,
                      flexShrink: 0,
                    }}
                  />
                ) : null}
                {opt.label}
              </span>
              {opt.count !== undefined && opt.count !== null && (
                <span style={{
                  fontSize: T.fsXs, fontWeight: T.fwBold,
                  background: sel ? T.blue : T.lightGray,
                  color: sel ? T.white : T.mid,
                  borderRadius: 8, padding: "1px 7px",
                }}>
                  {opt.count}
                </span>
              )}
            </div>
          );
        });
      })()}
    </div>
  ) : null;

  // P7 — `data-dropdown-wrapper` and `data-dropdown-trigger` are read by
  // PortfolioView's document-level click-outside + Escape handlers. Open/
  // close state is parent-owned (see header comment); these attributes let
  // the parent recognise "click is inside SOME open dropdown" without
  // each dropdown registering its own listener (single source of truth
  // for the side effect mirroring the single source of truth for the
  // state). Mirror the pattern in SortDropdown.jsx.
  return (
    <div data-dropdown-wrapper={id} style={{ position: "relative" }}>
      <button
        ref={triggerRef}
        type="button"
        data-testid={`filter-dropdown-${id}`}
        data-dropdown-trigger={id}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={onToggle}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: "5px 9px 5px 11px", borderRadius: 7,
          border: `1.5px solid ${isFiltered ? T.blue : T.border}`,
          background: isFiltered ? T.blueSoft : T.white,
          fontSize: T.fsSm5, fontWeight: T.fwSemibold,
          color: isFiltered ? T.blue : T.dark,
          cursor: "pointer", whiteSpace: "nowrap",
          transition: "border-color 0.12s, background 0.12s",
          maxWidth: triggerMaxWidth ?? "clamp(80px, 10vw, 160px)",
          overflow: "hidden",
        }}
      >
        <span style={{
          fontSize: T.fsXs, fontWeight: T.fwBold,
          color: isFiltered ? T.statBlue : T.light,
          marginRight: 1,
        }}>
          {label}
        </span>
        <span style={{
          fontWeight: T.fwSemibold,
          color: isFiltered ? T.dark : T.mid,
        }}>
          {currentVal}
        </span>
        <span style={{
          fontSize: T.fsXxs, color: T.light, marginLeft: 2,
          display: "inline-block",
          transform: isOpen ? "rotate(180deg)" : "none",
          transition: "transform 0.15s",
        }}>
          ▾
        </span>
      </button>

      {align === "right"
        ? menuNode && ReactDOM.createPortal(menuNode, document.body)
        : menuNode}
    </div>
  );
}
